<template>
  <div class="email-templates">
    <DevelopmentWarning />
    <Teleport to="body">
      <div v-if="activeMenu !== null" 
           class="menu-dropdown"
           :style="menuPosition">
        <button @click="editTemplate(getActiveTemplate())" class="menu-item">
          <i class="fas fa-edit"></i> Edit
        </button>
        <button @click="deleteTemplate(getActiveTemplate().id)" class="menu-item delete">
          <i class="fas fa-trash"></i> Delete
        </button>
      </div>
    </Teleport>

    <div class="header">
      <h2>Email Templates</h2>
      <button @click="showNewTemplateModal" class="btn btn-success">
        New Template
      </button>
    </div>

    <div class="templates-list">
      <table class="templates-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Subject</th>
            <th class="actions-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="template in templates" :key="template.id">
            <td>{{ template.name }}</td>
            <td>
              <span :class="['type-badge', template.type]">{{ template.type }}</span>
            </td>
            <td>{{ template.subject }}</td>
            <td class="actions-cell">
              <button 
                @click.stop="showMenuAt($event, template.id)" 
                class="kebab-button"
                ref="menuButton">
                ⋮
              </button>
            </td>
          </tr>
          <tr v-if="templates.length === 0">
            <td colspan="4" class="no-templates">No templates found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <EmailTemplateModal 
      v-if="showModal"
      @close="closeModal"
      @save="saveTemplate"
      :template="editingTemplate"
    />
  </div>
</template>

<script>
import EmailTemplateModal from './EmailTemplateModal.vue';
import DevelopmentWarning from './common/DevelopmentWarning.vue';
import { emailService } from '../services/api';

export default {
  name: 'EmailTemplateManager',
  provide() {
    return {
      closeModal: this.closeModal
    }
  },
  components: {
    EmailTemplateModal,
    DevelopmentWarning
  },
  data() {
    return {
      templates: [],
      showModal: false,
      editingTemplate: null,
      activeMenu: null,
      menuPosition: {
        top: '0px',
        left: '0px'
      },
      isDevelopment: process.env.NODE_ENV === 'development',
      devEmail: process.env.VUE_APP_DEV_EMAIL
    }
  },
  methods: {
    showMenuAt(event, id) {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      this.menuPosition = {
        top: `${rect.top - 10}px`,
        left: `${rect.left - 100}px`
      };
      this.activeMenu = id;
    },
    getActiveTemplate() {
      return this.templates.find(t => t.id === this.activeMenu);
    },
    handleClickOutside(event) {
      const menuEl = document.querySelector('.menu-dropdown');
      const isClickInside = event.target.closest('.kebab-button') || 
                           (menuEl && menuEl.contains(event.target));
      if (!isClickInside) {
        this.activeMenu = null;
      }
    },
    showNewTemplateModal() {
      this.editingTemplate = null;
      this.showModal = true;
    },
    editTemplate(template) {
      this.editingTemplate = { ...template }; // Clone to avoid direct mutation
      this.showModal = true;
      this.activeMenu = null; // Close the menu
    },
    closeModal() {
      this.showModal = false;
      this.editingTemplate = null;
    },
    async saveTemplate(templateData) {
      try {
        if (this.editingTemplate) {
          await emailService.updateTemplate(this.editingTemplate.id, templateData);
        } else {
          await emailService.createTemplate(templateData);
        }
        this.closeModal();
        await this.loadTemplates();
      } catch (error) {
        console.error('Error saving template:', error);
      }
    },
    async loadTemplates() {
      try {
        this.templates = await emailService.getTemplates();
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    },
    async deleteTemplate(id) {
      if (confirm('Are you sure you want to delete this template?')) {
        try {
          await emailService.deleteTemplate(id);
          await this.loadTemplates();
          this.activeMenu = null; // Close the menu
        } catch (error) {
          console.error('Error deleting template:', error);
        }
      }
      this.activeMenu = null; // Close the menu even if user cancels
    }
  },
  mounted() {
    this.loadTemplates();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
.email-templates {
  padding: 20px;
  background-color: #f0f9ff;
  min-height: calc(100vh - 60px);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.templates-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.templates-table th,
.templates-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.templates-table th {
  background: #f8f9fa;
  font-weight: 500;
  color: #4a5568;
  font-size: 0.875rem;
}

.templates-table td {
  font-size: 0.875rem;
  color: #4a5568;
}

.templates-table th:nth-child(1),
.templates-table td:nth-child(1) {
  width: 30%;
}

.templates-table th:nth-child(2),
.templates-table td:nth-child(2) {
  width: 20%;
}

.templates-table th:nth-child(3),
.templates-table td:nth-child(3) {
  width: 45%;
}

.templates-table th:nth-child(4),
.templates-table td:nth-child(4) {
  width: 5%;
  text-align: right;
}

.actions-cell {
  position: relative;
  width: 40px;
  text-align: center;
}

.kebab-menu {
  position: relative;
  display: inline-block;
}

.kebab-button {
  background: none;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  color: #4a5568;
  font-size: 18px;
}

.kebab-button:hover {
  color: #2d3748;
}

.menu-dropdown {
  position: fixed;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  min-width: 120px;
  margin-top: 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: #4a5568;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.menu-item:hover {
  background-color: #f7fafc;
}

.menu-item.delete {
  color: #e53e3e;
}

.menu-item.delete:hover {
  background-color: #fff5f5;
}

.menu-item i {
  font-size: 0.875rem;
}

.no-templates {
  text-align: center;
  color: #718096;
  padding: 32px !important;
}

.btn-success {
  background-color: #48bb78;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-success:hover {
  background-color: #38a169;
}

.templates-list {
  background: white;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  max-width: 1000px;
  margin: 0 auto;
}

.actions-header {
  text-align: right;
  padding-right: 24px;
}

.type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.type-badge.member {
  background-color: #ebf5ff;
  color: #2b6cb0;
}

.type-badge.speaker {
  background-color: #f0fff4;
  color: #2f855a;
}

.dev-warning {
  background-color: #fed7d7;
  color: #c53030;
  padding: 8px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  font-size: 0.875rem;
}
</style> 